import React, { useState, useEffect } from 'react';

const HomePage = () => {
  const [language, setLanguage] = useState('zh');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotating image carousel for hero section
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load theme from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
  }, []);

  // Save theme to localStorage when changed
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const heroImages = [
    "https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/4b98fb6d69119de1cd7a7c5aaa964635.jpeg",
    "https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/832a328301757c9e676e1722f5635ee4.jpeg",
    "https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/fe485778cbcf2e48f4d0fa5ffe60276d.jpeg"
  ];

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const content = {
    zh: {
      nav: {
        overview: '場地介紹',
        services: '服務項目', 
        equipment: '租借資訊',
        contact: '聯絡我們'
      },
      hero: {
        location: '香港元朗',
        title1: '專業影視',
        title2: '拍攝基地',
        specs: '25,000呎 | 4.5米樓底 | 4K LED螢幕',
        description: '專業影視拍攝基地,配備大型弧形 LED 螢幕及多元化拍攝區域。',
        cta1: '預約參觀',
        cta2: '查看設備',
        stats: [
          { number: '25,000', label: '平方呎拍攝空間', icon: 'ri-aspect-ratio-line' },
          { number: '4.5', label: '米樓底高度', icon: 'ri-ruler-2-line' },
          { number: '4K', label: 'LED螢幕解析度', icon: 'ri-hd-line' },
          { number: '100A', label: '三相電力供應', icon: 'ri-flashlight-line' }
        ]
      },
      overview: {
        title: '場地介紹',
        subtitle: '多功能拍攝空間,滿足各類製作需求',
        ledTitle: '弧形LED螢幕',
        ledDesc: '即時切換背景,減少後期成本',
        spaceTitle: '場地規格',
        spaceDesc: '支援大型場景搭建',
        facilityTitle: '完善配套',
        facilityDesc: '專業化妝間、會議室等設施'
      },
      services: {
        title: '服務項目',
        subtitle: '靈活租用方案,專業技術支援',
        tag: '按需報價',
        items: [
          { name: '全日租用', price: '按需報價' },
          { name: '半日租用', price: '按需報價' },
          { name: '小時租用', price: '按需報價' }
        ]
      },
      equipment: {
        title: '租借設備',
        subtitle: '提升製作效率與安全性的專業工具',
        cta: '查詢設備詳情',
        items: [
          '舞台系統', '金屬結構支架', '天花支架系統', 
          '移動式電動升降工作台', '大型鏟車', '片場木工室'
        ]
      },
      contact: {
        title: '立即預約參觀',
        subtitle: '專業團隊為您提供場地導覽與拍攝方案諮詢',
        formTitle: '聯絡表單',
        namePlaceholder: '姓名',
        emailPlaceholder: '電郵地址',
        phonePlaceholder: '聯絡電話',
        messagePlaceholder: '拍攝需求或查詢內容',
        submit: '提交查詢',
        phone1: 'Mr.Yau 9348 3793',
        phone2: 'Mrs.Wah 9161 1558',
        email: 'cs@BumbleBee101.com',
        address: '香港元朗麗新元朗中心101室'
      }
    },
    en: {
      nav: {
        overview: 'Studio Overview',
        services: 'Services',
        equipment: 'Equipment Rental',
        contact: 'Contact Us'
      },
      hero: {
        location: 'Yuen Long, Hong Kong',
        title1: 'Professional',
        title2: 'Film Studio',
        specs: '25,000 sq.ft | 4.5m Height | 4K LED Screen',
        description: 'Professional film studio equipped with large curved LED screens and diverse shooting areas.',
        cta1: 'Book Tour',
        cta2: 'View Equipment',
        stats: [
          { number: '25,000', label: 'sq.ft Shooting Space', icon: 'ri-aspect-ratio-line' },
          { number: '4.5', label: 'meters Ceiling Height', icon: 'ri-ruler-2-line' },
          { number: '4K', label: 'LED Screen Resolution', icon: 'ri-hd-line' },
          { number: '100A', label: 'Three-Phase Power', icon: 'ri-flashlight-line' }
        ]
      },
      overview: {
        title: 'Studio Overview',
        subtitle: 'Multi-functional shooting space for all production needs',
        ledTitle: 'Curved LED Screen',
        ledDesc: 'Real-time background switching, reducing post-production costs',
        spaceTitle: 'Space Specifications',
        spaceDesc: 'Supports large-scale set construction',
        facilityTitle: 'Complete Facilities',
        facilityDesc: 'Professional dressing rooms, meeting rooms and more'
      },
      services: {
        title: 'Services',
        subtitle: 'Flexible rental packages with professional technical support',
        tag: 'Custom Quote',
        items: [
          { name: 'Full Day Rental', price: 'Custom Quote' },
          { name: 'Half Day Rental', price: 'Custom Quote' },
          { name: 'Hourly Rental', price: 'Custom Quote' }
        ]
      },
      equipment: {
        title: 'Equipment Rental',
        subtitle: 'Professional tools to enhance production efficiency and safety',
        cta: 'Inquire Equipment Details',
        items: [
          'Stage Systems', 'Metal Structure Support', 'Ceiling Support System',
          'Mobile Electric Lift Platform', 'Large Forklift', 'Studio Workshop'
        ]
      },
      contact: {
        title: 'Book a Studio Tour',
        subtitle: 'Our professional team provides studio tours and shooting solution consultations',
        formTitle: 'Contact Form',
        namePlaceholder: 'Name',
        emailPlaceholder: 'Email Address',
        phonePlaceholder: 'Contact Number',
        messagePlaceholder: 'Shooting requirements or inquiry details',
        submit: 'Submit Inquiry',
        phone1: 'Mr.Yau 9348 3793',
        phone2: 'Mrs.Wah 9161 1558',
        email: 'cs@BumbleBee101.com',
        address: 'Room 101, Lai Sun Yuen Long Centre, Yuen Long, Hong Kong'
      }
    }
  };

  const t = content[language as keyof typeof content];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5pn36498t1oshnrdla0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-black text-white' 
        : 'bg-white text-gray-900'
    }`} style={{ fontFamily: '"Outfit", "Noto Sans TC", system-ui, -apple-system, sans-serif' }}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? isDarkMode 
            ? 'bg-black/90 backdrop-blur-xl border-b-2 border-[#FFFF00]/20' 
            : 'bg-white/90 backdrop-blur-md border-b-2 border-gray-200'
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Mobile: Two-row layout */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
            {/* Row 1 (mobile) / Left (desktop): Logo + Mobile Menu Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/438f2387c77f939110bc8482f2d2d85c.png" 
                  alt="Studio Logo"
                  className={`h-8 md:h-12 w-auto max-w-[140px] md:max-w-none object-contain transition-all duration-300 hover:scale-110 ${
                    isDarkMode ? '' : 'brightness-0'
                  }`}
                />
              </div>

              {/* Mobile: Right side controls + menu button */}
              <div className="flex items-center space-x-2 md:hidden">
                <button
                  onClick={toggleTheme}
                  className={`w-8 h-8 border-2 transition-all duration-300 flex items-center justify-center ${
                    isDarkMode
                      ? 'bg-black/80 border-[#FFFF00]/40 text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black hover:border-[#FFFF00] backdrop-blur-sm'
                      : 'bg-white border-[#FFFF00] text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black'
                  }`}
                  title={isDarkMode ? '切換到淺色模式' : '切換到深色模式'}
                >
                  <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-sm`}></i>
                </button>

                <button
                  onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
                  className={`w-12 h-8 border-2 text-xs font-medium tracking-wide transition-all duration-300 whitespace-nowrap flex items-center justify-center ${
                    isDarkMode
                      ? 'bg-black/80 border-[#FFFF00]/40 text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black hover:border-[#FFFF00] backdrop-blur-sm'
                      : 'bg-white border-[#FFFF00] text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <i className="ri-global-line text-xs"></i>
                    <span className="text-xs">{language === 'zh' ? 'EN' : '中'}</span>
                  </div>
                </button>

                <button
                  className={`transition-all duration-300 hover:scale-110 ${isDarkMode ? 'text-white/90' : 'text-gray-900'}`}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
                </button>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-12">
              <button 
                onClick={() => scrollToSection('overview')}
                className={`transition-all duration-300 relative group whitespace-nowrap text-sm font-medium tracking-wide hover:scale-105 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.overview}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 bg-[#FFFF00]"></span>
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className={`transition-all duration-300 relative group whitespace-nowrap text-sm font-medium tracking-wide hover:scale-105 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.services}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 bg-[#FFFF00]"></span>
              </button>
              <button 
                onClick={() => scrollToSection('equipment')}
                className={`transition-all duration-300 relative group whitespace-nowrap text-sm font-medium tracking-wide hover:scale-105 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.equipment}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 bg-[#FFFF00]"></span>
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`transition-all duration-300 relative group whitespace-nowrap text-sm font-medium tracking-wide hover:scale-105 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.contact}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 bg-[#FFFF00]"></span>
              </button>
            </div>

            {/* Desktop: Right side controls */}
            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className={`w-10 h-10 border-2 transition-all duration-300 flex items-center justify-center hover:scale-110 ${
                  isDarkMode
                    ? 'bg-black/80 border-[#FFFF00]/40 text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black hover:border-[#FFFF00] backdrop-blur-sm'
                    : 'bg-white border-[#FFFF00] text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black'
                }`}
                title={isDarkMode ? '切換到淺色模式' : '切換到深色模式'}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-base`}></i>
              </button>

              <button
                onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
                className={`w-14 h-10 border-2 text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap hover:scale-105 flex items-center justify-center ${
                  isDarkMode
                    ? 'bg-black/80 border-[#FFFF00]/40 text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black hover:border-[#FFFF00] backdrop-blur-sm'
                    : 'bg-white border-[#FFFF00] text-[#FFFF00] hover:bg-[#FFFF00] hover:text-black'
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-global-line text-sm"></i>
                  <span className="text-sm">{language === 'zh' ? 'EN' : '中'}</span>
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className={`md:hidden mt-3 pb-4 space-y-3 p-4 transition-all duration-300 animate-in slide-in-from-top border-2 ${
              isDarkMode ? 'bg-black/90 backdrop-blur-xl border-[#FFFF00]/20' : 'bg-white/90 backdrop-blur-sm border-gray-200'
            }`}>
              <button 
                onClick={() => scrollToSection('overview')}
                className={`block w-full text-left transition-all duration-300 py-2.5 text-sm font-medium tracking-wide hover:scale-105 hover:translate-x-2 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.overview}
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className={`block w-full text-left transition-all duration-300 py-2.5 text-sm font-medium tracking-wide hover:scale-105 hover:translate-x-2 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.services}
              </button>
              <button 
                onClick={() => scrollToSection('equipment')}
                className={`block w-full text-left transition-all duration-300 py-2.5 text-sm font-medium tracking-wide hover:scale-105 hover:translate-x-2 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.equipment}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`block w-full text-left transition-all duration-300 py-2.5 text-sm font-medium tracking-wide hover:scale-105 hover:translate-x-2 ${
                  isDarkMode ? 'text-white/90 hover:text-[#FFFF00]' : 'text-gray-900 hover:text-[#FFFF00]'
                }`}
              >
                {t.nav.contact}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        {/* Background Image Carousel */}
        <div className="absolute inset-0 fixed">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-2000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={image}
                alt={`Studio Background ${index + 1}`}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-black/70 via-black/60 to-black/80'
              : 'bg-gradient-to-b from-white/30 via-white/20 to-white/40'
          }`}></div>
          
          {/* Animated particles */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-[#FFFF00]/30 animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Left Content */}
          <div className="lg:col-span-2 space-y-8 animate-in slide-in-from-left duration-1000 pt-20 md:pt-0">
            <div className={`inline-block px-5 py-2.5 border-2 text-xs font-medium tracking-wider transition-all duration-300 hover:scale-110 ${
              isDarkMode 
                ? 'border-[#FFFF00]/40 text-[#FFFF00] bg-black/60 backdrop-blur-sm' 
                : 'border-[#FFFF00] text-[#FFFF00] bg-white/40 backdrop-blur-sm'
            }`}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#FFFF00] animate-pulse"></div>
                {t.hero.location}
              </div>
            </div>
            
            <div className="space-y-4">
              <h1 className="text-5xl lg:text-6xl font-black leading-none tracking-tight">
                <span className={`block drop-shadow-2xl transition-all duration-500 hover:scale-105 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{t.hero.title1}</span>
                <span className="block drop-shadow-2xl transition-all duration-500 hover:scale-105 bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent">{t.hero.title2}</span>
              </h1>
              <p className={`text-lg font-medium animate-in fade-in duration-1000 delay-300 ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>{t.hero.specs}</p>
              <p className={`text-base leading-relaxed max-w-lg animate-in fade-in duration-1000 delay-500 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>{t.hero.description}</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-bottom duration-1000 delay-700">
              {t.hero.stats.map((stat, index) => (
                <div key={index} className={`p-5 transition-all duration-300 hover:scale-105 border-2 ${
                  isDarkMode ? 'bg-black/60 backdrop-blur-sm border-[#FFFF00]/20' : 'bg-white/80 backdrop-blur-sm border-gray-200'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#FFFF00] flex items-center justify-center">
                      <i className={`${stat.icon} text-black text-base`}></i>
                    </div>
                    <div>
                      <div className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {stat.number}
                      </div>
                      <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4 animate-in slide-in-from-bottom duration-1000 delay-1000">
              <button 
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 font-semibold hover:scale-110 transition-all duration-300 shadow-xl whitespace-nowrap text-base tracking-wide group bg-[#FFFF00] text-black hover:shadow-[#FFFF00]/30"
              >
                {t.hero.cta1} 
                <i className="ri-arrow-right-up-line ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform text-lg"></i>
              </button>
              <button 
                onClick={() => scrollToSection('equipment')}
                className={`px-8 py-4 border-2 font-semibold transition-all duration-300 whitespace-nowrap text-base tracking-wide group ${
                  isDarkMode
                    ? 'border-[#FFFF00]/40 text-white hover:bg-[#FFFF00] hover:text-black hover:border-[#FFFF00] bg-black/60 backdrop-blur-sm'
                    : 'border-[#FFFF00] text-gray-900 bg-white/80 backdrop-blur-sm hover:bg-[#FFFF00] hover:text-black'
                }`}
              >
                {t.hero.cta2}
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform text-lg"></i>
              </button>
            </div>
          </div>

          {/* Right Content - Image Carousel Indicators */}
          <div className="lg:col-span-3 flex flex-col items-center lg:items-end justify-center space-y-6">
            <div className="flex gap-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 transition-all duration-300 ${
                    index === currentImageIndex 
                      ? 'bg-[#FFFF00] scale-125' 
                      : isDarkMode ? 'bg-white/30 hover:bg-white/50' : 'bg-gray-400 hover:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            <div className={`text-center p-6 backdrop-blur-sm border-2 ${
              isDarkMode ? 'bg-black/60 border-[#FFFF00]/20' : 'bg-white/80 backdrop-blur-sm border-gray-200'
            }`}>
              <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'}`}>
                {language === 'zh' ? '現正展示' : 'Currently Showing'}
              </div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {currentImageIndex === 0 && (language === 'zh' ? '攝影棚實景照片' : 'Studio Real Scene Photo')}
                {currentImageIndex === 1 && (language === 'zh' ? '弧形LED螢幕展示' : 'Curved LED Screen Display')}
                {currentImageIndex === 2 && (language === 'zh' ? '白色弧形攝影空間' : 'White Curved Photography Space')}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className={`w-6 h-10 border-2 flex justify-center ${
            isDarkMode ? 'border-white/30' : 'border-gray-600'
          }`}>
            <div className={`w-1 h-3 mt-2 animate-pulse ${
              isDarkMode ? 'bg-white/60' : 'bg-gray-600'
            }`}></div>
          </div>
        </div>
      </section>

      {/* Virtual Production Introduction Section */}
      <section className={`py-20 md:py-24 ${isDarkMode ? 'bg-gradient-to-b from-black to-gray-900' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 md:space-y-8">
              <div className={`inline-block px-4 py-2 border text-xs transition-all duration-300 hover:scale-110 ${
                isDarkMode 
                  ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                  : 'border-[#FFFF00] text-[#FFFF00]'
              }`}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#FFFF00] animate-pulse"></div>
                  {language === 'zh' ? '新一代製作基地' : 'Next-Gen Production Base'}
                </div>
              </div>

              <div className="space-y-4 md:space-y-6">
                <h2 className={`text-3xl md:text-4xl lg:text-5xl font-black leading-tight ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {language === 'zh' ? (
                    <>
                      <span className="block mb-2">Hong Kong</span>
                      <span className={`block bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent`}>
                        Virtual Production
                      </span>
                      <span className="block">& Film Base</span>
                    </>
                  ) : (
                    <>
                      <span className="block mb-2">Hong Kong</span>
                      <span className={`block bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent`}>
                        Virtual Production
                      </span>
                      <span className="block">& Film Base</span>
                    </>
                  )}
                </h2>

                {language === 'zh' && (
                  <h3 className={`text-xl md:text-2xl font-bold ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    香港新一代影視製作基地
                  </h3>
                )}

                <div className="space-y-4 md:space-y-6">
                  <p className={`text-base md:text-lg leading-relaxed ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {language === 'zh' 
                      ? 'BumbleBee101 聯同 StagePlus 打造 位於香港元朗的超大型影視製作基地，結合高端 LED 顯示技術、沉浸式場景系統與工業級拍攝空間，並整合專業虛擬製作流程與現場拍攝支援，打造一個更完整、高效且靈活的製作環境，專為廣告製作、影視拍攝、品牌內容及大型場景搭建而設。'
                      : 'BumbleBee101, in collaboration with StagePlus, is a large-scale production studio located in Yuen Long, Hong Kong. Combining high-end LED display technology, immersive scene systems, and industrial-grade filming space, the studio further integrates professional virtual production workflows and on-set support — creating a complete, efficient, and flexible production environment for commercials, film and TV, branded content, and large-scale set builds.'}
                  </p>

                  <div className={`p-6 border-2 transition-all duration-300 hover:scale-[1.02] ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-[#FFFF00]/10 to-[#E6E600]/10 border-[#FFFF00]/20 backdrop-blur-sm' 
                      : 'bg-gradient-to-r from-[#FFFF00]/20 to-[#E6E600]/20 border-[#FFFF00]/30'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-black text-base"></i>
                      </div>
                      <div>
                        <p className={`text-base md:text-lg font-medium leading-relaxed ${
                          isDarkMode ? 'text-white' : 'text-gray-800'
                        }`}>
                          {language === 'zh' ? (
                            <>
                              我們不只是一個片場,
                              <br />而是一個為創意而生的
                              <br />
                              <span className={`font-bold ${
                                isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                              }`}>Production Infrastructure Platform</span>。
                            </>
                          ) : (
                            <>
                              We are not just a studio,
                              <br />but a{' '}
                              <span className={`font-bold ${
                                isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                              }`}>Production Infrastructure Platform</span>{' '}
                              built for creativity.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {[
                  { icon: 'ri-tv-2-line', title: language === 'zh' ? '高端LED技術' : 'High-end LED Display', desc: 'High-end LED Display' },
                  { icon: 'ri-magic-line', title: language === 'zh' ? '沉浸式場景' : 'Immersive Scenes', desc: 'Immersive Scenes' },
                  { icon: 'ri-building-2-line', title: language === 'zh' ? '工業級空間' : 'Industrial Grade Space', desc: 'Industrial Grade Space' },
                  { icon: 'ri-settings-3-line', title: language === 'zh' ? '虛擬製作' : 'Virtual Production', desc: 'Virtual Production' }
                ].map((feature, index) => (
                  <div key={index} className={`p-4 transition-all duration-300 hover:scale-105 border ${
                    isDarkMode 
                      ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                      : 'bg-white border-gray-200 shadow-sm'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center">
                        <i className={`${feature.icon} text-black text-base`}></i>
                      </div>
                      <div>
                        <div className={`text-sm font-bold mb-1 leading-snug ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>{feature.title}</div>
                        {language === 'zh' && (
                          <div className={`text-xs ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>{feature.desc}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => scrollToSection('overview')}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 border whitespace-nowrap text-sm group ${
                  isDarkMode
                    ? 'border-[#FFFF00]/60 text-white hover:bg-[#FFFF00] hover:text-black bg-black/40 backdrop-blur-sm'
                    : 'border-[#FFFF00] text-gray-900 bg-white hover:bg-[#FFFF00] hover:text-black shadow-sm'
                }`}
              >
                {language === 'zh' ? '探索場地設施' : 'Explore Facilities'}
                <i className="ri-arrow-down-line ml-2 group-hover:translate-y-1 transition-transform"></i>
              </button>
            </div>

            {/* Right Content - Visual Elements */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-4 md:space-y-6">
                  <img 
                    src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/99a96799a0d2d317bfdd086fa2a2e68d.jpeg"
                    alt="Virtual Production Technology"
                    className="w-full h-48 md:h-56 object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <img 
                    src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/1fa76d99e62602a084c807e70dec629b.jpeg"
                    alt="LED Display Technology"
                    className="w-full h-32 md:h-40 object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="space-y-4 md:space-y-6 pt-8 md:pt-12">
                  <img 
                    src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/d67e30df21983f81f1b5e9b806a131eb.jpeg"
                    alt="Immersive Production Setup"
                    className="w-full h-32 md:h-40 object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <img 
                    src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/69cf7ee63e527d2cd5e5ff3250fab93e.jpeg"
                    alt="Production Infrastructure"
                    className="w-full h-48 md:h-56 object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#FFFF00]/20 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#FFFF00]/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className={`py-16 md:py-20 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8 md:mb-12">
            <div className="lg:col-span-1">
              <h2 className={`text-4xl md:text-5xl font-black mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <span className="text-[#FFFF00]">{language === 'zh' ? '場地' : 'Studio'}</span>{language === 'zh' ? '介紹' : ' Overview'}
              </h2>
            </div>
            <div className="lg:col-span-2 flex items-end">
              <p className={`text-base md:text-lg font-light ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>{t.overview.subtitle}</p>
            </div>
          </div>

          {/* Studio Layout Plan */}
          <div className="mb-6 md:mb-8">
            <div className={`p-6 md:p-8 transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl' : 'bg-white'
            }`}>
              <div className="text-center mb-6 md:mb-8">
                <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 border text-xs mb-3 md:mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '場地佈局圖' : 'Studio Layout Plan'}
                </div>
                <h3 className={`text-2xl md:text-3xl font-bold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{language === 'zh' ? '超越傳統片場的製作空間' : 'Beyond a Traditional Studio Environment'}</h3>
                <p className={`text-sm md:text-base mb-4 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {language === 'zh' 
                    ? 'BumbleBee101 佔地約 25,000 平方呎，樓底高度達 4.5 米，可同時支援大型製作團隊、複雜場景搭建及多機位拍攝。' 
                    : 'BumbleBee101 spans approximately 25,000 sq.ft. with a ceiling height of 4.5 metres, accommodating large production crews, complex set builds, and multi-camera setups.'
                  }
                </p>
                <p className={`text-sm leading-relaxed max-w-4xl mx-auto ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {language === 'zh'
                    ? '場地設計以「高效率製作流程」為核心，整合拍攝執行、虛擬製作流程與現場協調能力，讓導演、攝影師及品牌團隊能夠在同一空間內，流暢地完成從概念、搭建到拍攝的整體製作過程。'
                    : 'Designed around efficient production workflows, the space integrates on-set execution, virtual production processes, and real-time coordination — enabling directors, cinematographers, and brand teams to seamlessly move from concept and set build to filming within a single, cohesive environment.'}
                </p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto mb-8">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/3660a15b3a7b34b584be06d50570bd9a.jpeg"
                  alt="Studio Layout Plan"
                  className="w-full h-64 md:h-auto object-cover md:object-contain"
                />
              </div>

              {/* Suitable Scenarios Section */}
              <div className={`p-6 md:p-8 mb-6 border-2 ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                    <i className="ri-movie-2-line text-black text-base"></i>
                  </div>
                  <h4 className={`text-lg md:text-xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '適用場景' : 'Suitable Scenarios'}</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { 
                      icon: 'ri-advertisement-line', 
                      title: language === 'zh' ? '商業廣告拍攝' : 'Commercial Production',
                      subtitle: 'Commercial Production'
                    },
                    { 
                      icon: 'ri-film-line', 
                      title: language === 'zh' ? '影視製作' : 'Film & TV',
                      subtitle: 'Film & TV'
                    },
                    { 
                      icon: 'ri-smartphone-line', 
                      title: language === 'zh' ? '品牌內容與社交媒體影像' : 'Brand Content',
                      subtitle: 'Brand Content'
                    },
                    { 
                      icon: 'ri-music-line', 
                      title: language === 'zh' ? '音樂錄影帶' : 'Music Video',
                      subtitle: 'MV'
                    },
                    { 
                      icon: 'ri-movie-line', 
                      title: language === 'zh' ? '虛擬拍攝與沉浸式場景' : 'Virtual Production',
                      subtitle: 'Virtual Production'
                    },
                    { 
                      icon: 'ri-building-4-line', 
                      title: language === 'zh' ? '大型佈景搭建與藝術指導' : 'Set Construction',
                      subtitle: 'Set Construction'
                    }
                  ].map((scenario, index) => (
                    <div key={index} className={`p-4 transition-all duration-300 hover:scale-105 border-2 ${
                      isDarkMode 
                        ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                        : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${scenario.icon} text-black text-base`}></i>
                        </div>
                        <div>
                          <div className={`text-sm font-bold mb-1 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>{scenario.title}</div>
                          <div className={`text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>{scenario.subtitle}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Professional Production Facilities Section */}
              <div className={`p-6 md:p-8 border-2 ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                    <i className="ri-building-line text-black text-base"></i>
                  </div>
                  <h4 className={`text-lg md:text-xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '專業製作配套設施' : 'Professional Production Facilities'}</h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {language === 'zh' 
                      ? '為配合高端製作流程，BumbleBee101 提供完整後勤配套' 
                      : 'To support high-end production workflows, BumbleBee101 provides comprehensive logistical support'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {[
                    { 
                      icon: 'ri-user-star-line', 
                      title: language === 'zh' ? '私人化妝間' : 'Private Makeup Room',
                      desc: language === 'zh' ? '2人' : '2 People'
                    },
                    { 
                      icon: 'ri-group-line', 
                      title: language === 'zh' ? '大型化妝間' : 'Large Makeup Room',
                      desc: language === 'zh' ? '可容納5人同時使用' : 'Accommodates 5 people'
                    },
                    { 
                      icon: 'ri-shirt-line', 
                      title: language === 'zh' ? '更衣室' : 'Changing Room',
                      desc: language === 'zh' ? '寬敞舒適' : 'Spacious & Comfortable'
                    },
                    { 
                      icon: 'ri-presentation-line', 
                      title: language === 'zh' ? '會議室' : 'Meeting Room',
                      desc: language === 'zh' ? '策劃與協作空間' : 'Planning & Collaboration'
                    },
                    { 
                      icon: 'ri-door-line', 
                      title: language === 'zh' ? '洗手間' : 'Restroom',
                      desc: language === 'zh' ? '完善衛生設施' : 'Complete Facilities'
                    },
                    { 
                      icon: 'ri-hammer-line', 
                      title: language === 'zh' ? '片場木工室' : 'Set Workshop',
                      desc: language === 'zh' ? '場景搭建專用' : 'For Set Construction'
                    }
                  ].map((facility, index) => (
                    <div key={index} className={`p-4 transition-all duration-300 hover:scale-105 border-2 ${
                      isDarkMode 
                        ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                        : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${facility.icon} text-black text-base`}></i>
                        </div>
                        <div>
                          <div className={`text-sm font-bold mb-1 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>{facility.title}</div>
                          <div className={`text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>{facility.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`p-4 border-2 ${
                  isDarkMode 
                    ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-black text-sm"></i>
                    </div>
                    <p className={`text-sm leading-relaxed ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      {language === 'zh'
                        ? '這些設施讓製作團隊能夠在同一空間內完成策劃、搭建、拍攝與協作。'
                        : 'These facilities enable production teams to complete planning, construction, shooting, and collaboration all in one space.'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Shooting Areas Section - NEW */}
              <div className={`p-6 md:p-8 ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm' 
                  : 'bg-white shadow-sm'
              }`}>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                    <i className="ri-camera-3-line text-black text-base"></i>
                  </div>
                  <h4 className={`text-lg md:text-xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '拍攝區' : 'Shooting Areas'}</h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {language === 'zh' 
                      ? '多元化拍攝空間，滿足各類製作需求' 
                      : 'Diverse shooting spaces to meet various production needs'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                      isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                    }`}>
                      <i className="ri-tv-2-line text-black text-base"></i>
                    </div>
                    <h4 className={`font-bold mb-2 text-sm ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{language === 'zh' ? '弧形LED螢幕' : 'Curved LED Screen'}</h4>
                    <p className={`text-xs ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>{language === 'zh' ? '高解析度LED背景，實時切換場景' : 'High-resolution LED background, real-time scene switching'}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                      isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                    }`}>
                      <i className="ri-building-2-line text-black text-base"></i>
                    </div>
                    <h4 className={`font-bold mb-2 text-sm ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{language === 'zh' ? '多功能區域' : 'Multi-Function Areas'}</h4>
                    <p className={`text-xs ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>{language === 'zh' ? '化妝間、會議室、儲物空間一應俱全' : 'Makeup rooms, meeting rooms, storage spaces all available'}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                      isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                    }`}>
                      <i className="ri-aspect-ratio-line text-black text-base"></i>
                    </div>
                    <h4 className={`font-bold mb-2 text-sm ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{language === 'zh' ? '大型拍攝空間' : 'Large Shooting Space'}</h4>
                    <p className={`text-xs ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>{language === 'zh' ? '支援各種規模的拍攝項目' : 'Supports various scale shooting projects'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Studio Entrance */}
          <div className="mb-6 md:mb-8">
            <div className={`p-6 md:p-8 transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl' : 'bg-white'
            }`}>
              <div className="text-center mb-6 md:mb-8">
                <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 border text-xs mb-3 md:mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '片場入口' : 'Studio Entrance'}
                </div>
                <h3 className={`text-xl md:text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{language === 'zh' ? '便利車輛進出' : 'Convenient Vehicle Access'}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{language === 'zh' ? '寬敞入口設計，車輛可直接進入場地，方便大型設備運輸' : 'Spacious entrance design, vehicles can enter directly, convenient for large equipment transport'}</p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/1d7fcd23f485fedc4738eb776ba5055c.jpeg"
                  alt="Studio Entrance"
                  className="w-full h-64 md:h-auto object-cover md:object-contain"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-6 md:mt-8">
                <div className="text-center">
                  <div className={`w-8 h-8 md:w-11 md:h-11 flex items-center justify-center mx-auto mb-2 md:mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-truck-line text-black text-sm md:text-lg"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '車輛直入' : 'Direct Vehicle Access'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '寬敞入口，車輛可直接進入場地' : 'Spacious entrance, vehicles can enter directly'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-8 h-8 md:w-11 md:h-11 flex items-center justify-center mx-auto mb-2 md:mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-settings-3-line text-black text-sm md:text-lg"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '設備運輸' : 'Equipment Transport'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '方便大型攝影設備及道具搬運' : 'Convenient for transporting large filming equipment and props'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-8 h-8 md:w-11 md:h-11 flex items-center justify-center mx-auto mb-2 md:mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-time-line text-black text-sm md:text-lg"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '高效進場' : 'Efficient Setup'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '節省設置時間，提升拍攝效率' : 'Save setup time, improve filming efficiency'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Studio Specifications */}
          <div className="mb-12 md:mb-16">
            <div className={`p-6 md:p-8 border transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <div className="text-center mb-6 md:mb-8">
                <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 border text-xs mb-3 md:mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '工業級製作環境' : 'Industrial-Scale Production Environment'}
                </div>
                <h3 className={`text-xl md:text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {language === 'zh' ? '工業級影視製作空間' : 'Industrial-Scale Film Production Space'}
                </h3>
                <p className={`text-sm mb-4 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {language === 'zh' 
                    ? 'BumbleBee101 是為大型製作而設的拍攝基地' 
                    : 'BumbleBee101 is a production base designed for large-scale productions'}
                </p>
              </div>

              {/* 工業級特色區塊 */}
              <div className={`p-6 md:p-8 mb-6 border ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                    <i className="ri-building-4-line text-black text-base"></i>
                  </div>
                  <h4 className={`text-xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {language === 'zh' ? '工業級製作環境特色' : 'Industrial-Scale Production Features'}
                  </h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {language === 'zh' ? '專為大型專業製作而建' : 'Built for Professional Large-Scale Productions'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {[
                    { 
                      icon: 'ri-aspect-ratio-line', 
                      title: language === 'zh' ? '超大面積拍攝區域' : 'Massive Shooting Area',
                      desc: language === 'zh' ? 'Large-Scale Shooting Area' : 'Expansive Production Space'
                    },
                    { 
                      icon: 'ri-building-2-line', 
                      title: language === 'zh' ? '支援大型佈景搭建' : 'Large Set Construction Support',
                      desc: language === 'zh' ? 'Large Set Construction Support' : 'Professional Set Building Capabilities'
                    },
                    { 
                      icon: 'ri-camera-3-line', 
                      title: language === 'zh' ? '多機位拍攝配置' : 'Multi-Camera Setup Configuration',
                      desc: language === 'zh' ? 'Multi-Camera Setup Configuration' : 'Advanced Camera Positioning Systems'
                    },
                    { 
                      icon: 'ri-shield-check-line', 
                      title: language === 'zh' ? '高承載力場地結構' : 'High Load-Bearing Structure',
                      desc: language === 'zh' ? 'High Load-Bearing Structure' : 'Industrial-Grade Infrastructure'
                    },
                    { 
                      icon: 'ri-truck-line', 
                      title: language === 'zh' ? '支援大型設備與車輛進場' : 'Large Equipment & Vehicle Access',
                      desc: language === 'zh' ? 'Large Equipment & Vehicle Access' : 'Heavy Equipment Transportation Support'
                    }
                  ].map((feature, index) => (
                    <div key={index} className={`p-4 transition-all duration-300 hover:scale-105 border ${
                      isDarkMode 
                        ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                        : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${feature.icon} text-black text-base`}></i>
                        </div>
                        <div>
                          <div className={`text-sm font-bold mb-1 leading-snug ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>{feature.title}</div>
                          <div className={`text-xs ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>{feature.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`p-4 border ${
                  isDarkMode 
                    ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-movie-2-line text-black text-base"></i>
                    </div>
                    <p className={`text-sm leading-relaxed ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      {language === 'zh' ? (
                        <>
                          這是一個真正為<span className={`font-bold ${
                            isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                          }`}>「專業製作」</span>而存在的空間。
                        </>
                      ) : (
                        <>
                          This is a space truly built for{' '}
                          <span className={`font-bold ${
                            isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                          }`}>professional production</span>.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="w-full max-w-5xl mx-auto">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/aa08a4c4372578aa138c25542710e6c2.jpeg"
                  alt="Studio Specifications"
                  className="w-full h-64 md:h-auto object-cover md:object-contain"
                />
              </div>
            </div>
          </div>

          {/* LED Screen Display */}
          <div className="mb-16">
            <div className={`p-8 transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl' : 'bg-white'
            }`}>
              <div className="text-center mb-8">
                <div className={`inline-block px-4 py-2 border text-xs mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '弧形LED虛擬舞台' : 'Curved LED Virtual Stage'}
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {language === 'zh' ? '沉浸式弧形 LED 虛擬拍攝舞台' : 'Immersive Curved LED Virtual Stage'}
                </h3>
                <p className={`text-base md:text-lg leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {language === 'zh' 
                    ? 'BumbleBee101 的核心亮點，是一組大型弧形 LED 顯示系統，為影像創作提供極高自由度與視覺張力。'
                    : 'BumbleBee101&apos;s core highlight is a large curved LED display system that provides exceptional creative freedom and visual impact for filmmaking.'
                  }
                </p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto mb-8">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/3d76b046e8bb71e029deed0f0aa3daff.jpeg"
                  alt="Curved LED Virtual Stage"
                  className="w-full h-auto object-contain"
                />
              </div>

              {/* 技術規格區塊 */}
              <div className={`p-6 mb-8 border transition-all duration-300 hover:scale-[1.02] ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                    <i className="ri-settings-3-line text-black text-lg"></i>
                  </div>
                  <h4 className={`text-xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {language === 'zh' ? '技術規格' : 'Technical Specifications'}
                  </h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {language === 'zh' ? 'Technical Specifications' : 'Professional Grade LED Technology'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {[
                    { 
                      icon: 'ri-aspect-ratio-line', 
                      title: language === 'zh' ? '尺寸規格' : 'Dimensions',
                      value: language === 'zh' ? '高 4.1 米 × 寬 10 米' : '4.1m Height × 10m Width',
                      subtitle: language === 'zh' ? '弧形結構' : 'Curved Structure',
                      desc: language === 'zh' ? 'Curved Structure' : 'Advanced Curved Design'
                    },
                    { 
                      icon: 'ri-hd-line', 
                      title: language === 'zh' ? '解像度' : 'Resolution',
                      value: '7296 × 1536',
                      subtitle: language === 'zh' ? '4K 視覺效果' : '4K Visual Quality',
                      desc: language === 'zh' ? '4K Visual Quality' : 'Ultra-High Definition Display'
                    },
                    { 
                      icon: 'ri-refresh-line', 
                      title: language === 'zh' ? '刷新率' : 'Refresh Rate',
                      value: '50Hz',
                      subtitle: language === 'zh' ? '流暢顯示' : 'Smooth Display',
                      desc: language === 'zh' ? 'Smooth Display' : 'Flicker-Free Performance'
                    }
                  ].map((spec, index) => (
                    <div key={index} className={`p-4 transition-all duration-300 hover:scale-105 border-2 ${
                      isDarkMode 
                        ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                        : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                      <div className="text-center">
                        <div className="w-10 h-10 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
                          <i className={`${spec.icon} text-black text-base`}></i>
                        </div>
                        <div className={`text-sm font-bold mb-1 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{spec.title}</div>
                        <div className={`text-lg font-black mb-1 ${
                          isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                        }`}>{spec.value}</div>
                        <div className={`text-xs font-medium mb-1 ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>{spec.subtitle}</div>
                        <div className={`text-xs ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>{spec.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`p-4 border ${
                  isDarkMode 
                    ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-magic-line text-black text-sm"></i>
                    </div>
                    <p className={`text-sm leading-relaxed ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      {language === 'zh'
                        ? '適用於虛擬背景、沉浸式場景與動態影像呈現'
                        : 'Suitable for virtual backgrounds, immersive scenes, and dynamic visual presentations'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* 品牌標語區塊 */}
              <div className={`p-6 transition-all duration-300 hover:scale-[1.02] ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm' 
                  : 'bg-white shadow-sm'
              }`}>
                <div className="text-center">
                  <h2 className={`text-3xl md:text-4xl font-bold leading-snug tracking-tight ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {language === 'zh' ? (
                      <>
                        BumbleBee101 的 LED 虛擬舞台，<span className="text-[#FFFF00]">讓創意不再受限於實體場景</span>，而是透過影像與製作技術，重新定義空間。
                      </>
                    ) : (
                      <>
                        BumbleBee101&apos;s LED virtual stage <span className="text-[#FFFF00]">removes the limitations of physical sets</span>, redefining space through the integration of visual and production technology.
                      </>
                    )}
                  </h2>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-hd-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '4K超高清' : '4K Ultra HD'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '7296×1536解像度，畫面細膩清晰' : '7296×1536 resolution, detailed and clear images'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-refresh-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '50Hz刷新率' : '50Hz Refresh Rate'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '流暢顯示，無閃爍現象' : 'Smooth display, no flicker'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-magic-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '虛擬場景' : 'Virtual Scenes'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '即時切換背景，節省製作成本' : 'Real-time background switching, reduce production costs'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Alternative Shooting Space */}
          <div className="mb-16">
            <div className={`p-8 border transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <div className="text-center mb-8">
                <div className={`inline-block px-4 py-2 border text-xs mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '多功能拍攝區域' : 'Multi-Function Shooting Area'}
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{language === 'zh' ? '專業場景搭建空間' : 'Professional Set Construction Space'}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{language === 'zh' ? '10米寬 × 4.2米高 × 4米深 | 支援綠幕及場景搭建' : '10m Width × 4.2m Height × 4m Depth | Supports green screen and set construction'}</p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/6b9a8f5820cfb138b925daa8bdab73c2.jpeg"
                  alt="Alternative Shooting Space"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-layout-grid-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '場景搭建' : 'Set Construction'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '寬敞空間，支援各類場景搭建' : 'Spacious area, supports various set constructions'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-film-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '綠幕拍攝' : 'Green Screen Shooting'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '專業綠幕設置，後期合成更靈活' : 'Professional green screen setup, flexible post-production compositing'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-ruler-2-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '充足空間' : 'Ample Space'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '10米寬×4.2米高×4米深的專業尺寸' : '10m width × 4.2m height × 4m depth professional dimensions'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Facilities */}
          <div className="mb-16">
            <div className={`p-8 border transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <div className="text-center mb-8">
                <div className={`inline-block px-4 py-2 border text-xs mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '專業配套設施' : 'Professional Facilities'}
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{language === 'zh' ? '完善的技術支援' : 'Comprehensive Technical Support'}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{language === 'zh' ? '100A三相電 | 免費5G/2.4G WiFi | 後台螢幕控制系統' : '100A Three-Phase Power | Free 5G/2.4G WiFi | Backstage Screen Control System'}</p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/babebff4139dabf24547d8150e5fcc2e.jpeg"
                  alt="Professional Facilities"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-flashlight-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '100A三相電' : '100A Three-Phase Power'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '強大電力供應，支援大型設備運作' : 'Powerful electrical supply, supports large equipment operation'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-wifi-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '免費WiFi' : 'Free WiFi'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '5G/2.4G雙頻網絡，高速穩定連接' : '5G/2.4G dual-band network, high-speed stable connection'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-computer-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '後台控制系統' : 'Backstage Control System'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '專業螢幕控制，操作簡便高效' : 'Professional screen control, easy and efficient operation'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Makeup Rooms */}
          <div className="mb-16">
            <div className={`p-8 border transition-all duration-300 hover:scale-[1.01] ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <div className="text-center mb-8">
                <div className={`inline-block px-4 py-2 border text-xs mb-4 ${
                  isDarkMode 
                    ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' 
                    : 'border-[#FFFF00] text-[#FFFF00]'
                }`}>
                  {language === 'zh' ? '專業化妝間' : 'Professional Makeup Rooms'}
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{language === 'zh' ? '寬敞舒適的化妝空間' : 'Spacious and Comfortable Makeup Space'}</h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{language === 'zh' ? '5人後台化妝間 | 獨立個人化妝間 | 專業化妝設備' : '5-Person Backstage Makeup Room | Individual Private Makeup Room | Professional Makeup Equipment'}</p>
              </div>
              
              <div className="w-full max-w-5xl mx-auto">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/745b4ac0fbccb36880643eace586e713.jpeg"
                  alt="Makeup Rooms"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-group-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '5人後台化妝間' : '5-Person Backstage Makeup Room'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '寬敞空間，可同時為多人進行化妝' : 'Spacious area, can accommodate multiple people for makeup simultaneously'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-user-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '獨立個人化妝間' : 'Individual Private Makeup Room'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '私密空間，提供個人化服務' : 'Private space, providing personalized service'}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-11 h-11 flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDarkMode ? 'bg-[#FFFF00]/90 backdrop-blur-sm' : 'bg-[#FFFF00]'
                  }`}>
                    <i className="ri-magic-line text-black text-base"></i>
                  </div>
                  <h4 className={`font-bold mb-2 text-sm ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '專業設備' : 'Professional Equipment'}</h4>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{language === 'zh' ? '專業化妝燈光與鏡台，效果更佳' : 'Professional makeup lighting and vanity tables, better results'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className={`py-16 md:py-20 ${isDarkMode ? 'bg-gray-900/60 backdrop-blur-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8 md:mb-12">
            <div>
              <h2 className={`text-4xl md:text-5xl font-black mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <span className="text-[#FFFF00]">{language === 'zh' ? '服務' : 'Service'}</span>{language === 'zh' ? '項目' : 's'}
              </h2>
              <p className={`text-base md:text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>{t.services.subtitle}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {t.services.items.map((item, index) => (
              <div key={index} className={`p-4 md:p-6 hover:scale-105 transition-all duration-300 border ${
                isDarkMode 
                  ? 'bg-black/60 backdrop-blur-xl border-white/10 text-white' 
                  : 'bg-gray-50 border border-gray-200 text-gray-900'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <i className={`ri-film-line text-base ${
                    isDarkMode ? 'text-[#FFFF00]' : 'text-[#FFFF00]'
                  }`}></i>
                  <h3 className="text-base font-bold">{item.name}</h3>
                </div>
                <p className={`text-xs mb-3 md:mb-4 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {index === 0 && (language === 'zh' ? '提供全天候的場地租用服務，滿足長時間拍攝需求' : 'Provides all-day studio rental services for long-duration shooting requirements')}
                  {index === 1 && (language === 'zh' ? '靈活的半日租用方案，適合中小型拍攝項目' : 'Flexible half-day rental packages, suitable for small to medium shooting projects')}
                  {index === 2 && (language === 'zh' ? '按小時計費的靈活租用方式，經濟實惠' : 'Flexible hourly rental with cost-effective pricing')}
                </p>
                <hr className={`mt-3 md:mt-4 ${
                  isDarkMode ? 'border-white/20' : 'border-gray-300'
                }`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section id="equipment" className={`py-16 md:py-20 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className={`inline-block px-3 md:px-4 py-1.5 md:py-2 border text-xs mb-4 md:mb-6 ${
                isDarkMode ? 'border-[#FFFF00]/60 text-[#FFFF00] bg-black/40 backdrop-blur-sm' : 'border-[#FFFF00] text-[#FFFF00]'
              }`}>
                {language === 'zh' ? '專業設備' : 'Professional Equipment'}
              </div>
              <img 
                src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/f036ba7569cb70647176978f663eaba5.jpeg"
                alt="Equipment"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>

            <div className="lg:col-span-3 space-y-6 md:space-y-8 order-1 lg:order-2">
              <div>
                <h2 className={`text-4xl md:text-5xl font-black leading-tight mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  <span className="block">{t.equipment.title}</span>
                </h2>
                <p className={`text-base md:text-lg leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{t.equipment.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {t.equipment.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <i className="ri-tools-fill text-[#FFFF00] text-base"></i>
                    <span className={`font-medium text-sm ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{item}</span>
                  </div>
                ))}
              </div>

              <button className={`px-5 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl font-medium transition-colors whitespace-nowrap text-sm border ${
                isDarkMode 
                  ? 'bg-black/60 backdrop-blur-xl border-white/10 text-white hover:bg-white/10' 
                  : 'bg-gray-900 text-white hover:bg-gray-700'
              }`}>
                {t.equipment.cta}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className={`py-16 md:py-20 ${
        isDarkMode 
          ? 'bg-gray-900/60 backdrop-blur-sm' 
          : 'bg-gradient-to-b from-white to-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className={`text-4xl md:text-5xl font-black mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{t.contact.title}</h2>
            <p className={`text-base md:text-lg mb-2 max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>{t.contact.subtitle}</p>
            <p className={`text-sm max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-400' : 'text-gray-700'
            }`}>
              {language === 'zh' ? '如有任何諮詢，歡迎透過以下方式聯絡我們，我們團隊將盡快回覆您的查詢。' : 'If you have any inquiries, please contact us through the following methods. Our team will respond to your queries as soon as possible.'}
            </p>
          </div>
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Left: Contact Form */}
            <div className={`p-6 md:p-8 h-full border transition-all duration-300 order-2 lg:order-1 ${
              isDarkMode ? 'bg-black/60 backdrop-blur-xl border-white/10' : 'bg-white border border-gray-200 shadow-lg'
            }`}>
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-message-3-line text-black text-base md:text-xl"></i>
                </div>
                <h3 className={`text-lg md:text-xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{t.contact.formTitle}</h3>
              </div>
              
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>{language === 'zh' ? '姓名 *' : 'Name *'}</label>
                    <input
                      type="text"
                      name="name"
                      placeholder={t.contact.namePlaceholder}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full p-3 md:p-4 border text-sm focus:outline-none focus:ring-2 transition-all ${
                        isDarkMode
                          ? 'bg-black/60 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:ring-[#FFFF00]/50 focus:border-[#FFFF00]/50'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-[#FFFF00] focus:border-[#FFFF00]'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>{language === 'zh' ? '電郵 *' : 'Email *'}</label>
                    <input
                      type="email"
                      name="email"
                      placeholder={t.contact.emailPlaceholder}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full p-3 md:p-4 border text-sm focus:outline-none focus:ring-2 transition-all ${
                        isDarkMode
                          ? 'bg-black/60 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:ring-[#FFFF00]/50 focus:border-[#FFFF00]/50'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-[#FFFF00] focus:border-[#FFFF00]'
                      }`}
                    />
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>{language === 'zh' ? '聯絡電話 *' : 'Contact Phone *'}</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t.contact.phonePlaceholder}
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full p-3 md:p-4 border text-sm focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-black/60 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:ring-[#FFFF00]/50 focus:border-[#FFFF00]/50'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-[#FFFF00] focus:border-[#FFFF00]'
                    }`}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>{language === 'zh' ? '拍攝需求 *' : 'Shooting Requirements *'}</label>
                  <textarea
                    name="message"
                    placeholder={t.contact.messagePlaceholder}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className={`w-full p-3 md:p-4 border text-sm focus:outline-none focus:ring-2 resize-none transition-all ${
                      isDarkMode
                        ? 'bg-black/60 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:ring-[#FFFF00]/50 focus:border-[#FFFF00]/50'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-[#FFFF00] focus:border-[#FFFF00]'
                    }`}
                  />
                  <div className={`text-xs mt-1 text-right ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {formData.message.length}/500
                  </div>
                </div>
                
                {submitStatus === 'success' && (
                  <div className={`p-3 md:p-4 border ${
                    isDarkMode
                      ? 'bg-green-600/20 border-green-500/30 text-green-400'
                      : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-green-600/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-xs md:text-sm"></i>
                      </div>
                      <span className="text-sm font-medium">{language === 'zh' ? '查詢已成功提交！我們將盡快回覆您。' : 'Inquiry submitted successfully! We will reply to you as soon as possible.'}</span>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className={`p-3 md:p-4 border ${
                    isDarkMode
                      ? 'bg-red-600/20 border-red-500/30 text-red-400'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-red-600/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line text-xs md:text-sm"></i>
                      </div>
                      <span className="text-sm font-medium">{language === 'zh' ? '提交失敗，請重試或直接聯絡我們。' : 'Submission failed, please try again or contact us directly.'}</span>
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 md:py-4 font-medium hover:scale-105 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap text-sm ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-[#FFFF00] to-[#E6E600] text-black hover:shadow-[#FFFF00]/25'
                      : 'bg-gradient-to-r from-[#FFFF00] to-[#E6E600] text-black hover:shadow-[#FFFF00]/25'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-xs md:text-sm"></i>
                      {language === 'zh' ? '提交中...' : 'Submitting...'}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line text-xs md:text-sm"></i>
                      {t.contact.submit}
                    </span>
                  )}
                </button>
              </form>
            </div>

            {/* Right: Contact Information */}
            <div className="space-y-6 md:space-y-8 order-1 lg:order-2">
              {/* Contact Cards */}
              <div className="space-y-4 md:space-y-6">
                {/* Phone Card */}
                <div className={`p-4 md:p-6 transition-all duration-300 hover:scale-105 border ${
                  isDarkMode 
                    ? 'bg-black/60 backdrop-blur-xl border-white/10' 
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}>
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-black text-base md:text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-base md:text-lg font-bold mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{language === 'zh' ? '聯絡電話' : 'Contact Phone'}</h4>
                      <p className={`text-sm mb-2 md:mb-3 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>{language === 'zh' ? '發送詳細查詢或預約請求' : 'Send detailed inquiries or booking requests'}</p>
                      <div className="space-y-1">
                        <a href={`tel:${t.contact.phone1}`} className={`block font-bold text-sm md:text-base hover:text-[#FFFF00] transition-colors break-all ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {t.contact.phone1}
                        </a>
                        <a href={`tel:${t.contact.phone2}`} className={`block font-bold text-sm md:text-base hover:text-[#FFFF00] transition-colors break-all ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {t.contact.phone2}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email Card */}
                <div className={`p-4 md:p-6 transition-all duration-300 hover:scale-105 border ${
                  isDarkMode 
                    ? 'bg-black/60 backdrop-blur-xl border-white/10' 
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}>
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-black text-base md:text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-base md:text-lg font-bold mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{language === 'zh' ? '電郵地址' : 'Email Address'}</h4>
                      <p className={`text-sm mb-2 md:mb-3 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>{language === 'zh' ? '發送詳細查詢或預約請求' : 'Send detailed inquiries or booking requests'}</p>
                      <a href={`mailto:${t.contact.email}`} className={`font-bold text-sm md:text-base hover:text-[#FFFF00] transition-colors break-all ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {t.contact.email}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address Card */}
                <div className={`p-4 md:p-6 transition-all duration-300 hover:scale-105 border ${
                  isDarkMode 
                    ? 'bg-black/60 backdrop-blur-xl border-white/10' 
                    : 'bg-white border border-gray-200'
                }`}>
                  <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-black text-base md:text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-base md:text-lg font-bold mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{language === 'zh' ? '場地地址' : 'Studio Address'}</h4>
                      <p className={`text-sm mb-2 md:mb-3 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>{language === 'zh' ? '歡迎預約實地參觀' : 'Welcome to book a site visit'}</p>
                      <p className={`font-bold text-sm md:text-base ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{t.contact.address}</p>
                    </div>
                  </div>
                  
                  {/* Google Map */}
                  <div className="overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.5829885775437!2d114.0267!3d22.4462!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x340406f6ca8f0001%3A0x5bb9a4e5b16a1234!2sLai%20Sun%20Yuen%20Long%20Centre%2C%20Yuen%20Long%2C%20Hong%20Kong!5e0!3m2!1sen!2sus!4v1647123456789!5m2!1sen!2sus"
                      width="100%"
                      height="160"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={language === 'zh' ? '場地位置地圖' : 'Studio Location Map'}
                      className="md:h-48"
                    />
                  </div>
                </div>

                {/* Business Hours Card - Removed and replaced with Quick Response Promise */}
                <div className={`p-4 md:p-6 text-center border ${
                  isDarkMode 
                    ? 'bg-black/60 backdrop-blur-xl border-white/10' 
                    : 'bg-white border border-gray-200'
                }`}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className="ri-customer-service-2-line text-black text-base md:text-lg"></i>
                  </div>
                  <h4 className={`text-base md:text-lg font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{language === 'zh' ? '專業服務承諾' : 'Professional Service Promise'}</h4>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    {language === 'zh'
                      ? '24小時內回覆查詢 • 免費場地導覽 • 專業拍攝方案諮詢'
                      : '24-hour inquiry response • Free studio tour • Professional shooting solution consultation'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Brand Positioning Section */}
          <div className={`mt-16 md:mt-20 p-8 md:p-12 text-center border ${
            isDarkMode 
              ? 'bg-black/60 backdrop-blur-xl border-white/10' 
              : 'bg-white border border-gray-200'
          }`}>
            <div className="max-w-4xl mx-auto">
              {/* Main Title */}
              <h2 className={`text-3xl md:text-4xl lg:text-5xl font-black mb-6 md:mb-8 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                A New Standard for<br />
                <span className="bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent">
                  Hong Kong Production
                </span>
              </h2>

              {/* Brand Positioning Description */}
              <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
                <p className={`text-lg md:text-xl font-medium leading-relaxed ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {language === 'zh' ? (
                    <>
                      BumbleBee101 的定位不只是片場,<br />
                      而是香港新一代影視製作基礎設施。
                    </>
                  ) : (
                    <>
                      BumbleBee101 is positioned not just as a studio,<br />
                      but as Hong Kong's next-generation film production infrastructure.
                    </>
                  )}
                </p>

                <p className={`text-base md:text-lg leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {language === 'zh' ? (
                    <>
                      在整合虛擬製作流程與專業拍攝支援的基礎上，聯同具備豐富行業經驗的虛擬製作團隊，為創作者提供更完整、高效且靈活的製作環境。
                    </>
                  ) : (
                    <>
                      By integrating virtual production workflows and professional on-set support — alongside an experienced virtual production team — we provide creators with a more complete, efficient, and flexible production environment.
                    </>
                  )}
                </p>

                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {language === 'zh' ? '我們致力於為創作者提供:' : 'We are committed to providing creators with:'}
                </p>
              </div>

              {/* Core Commitments Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
                {[
                  { 
                    icon: 'ri-aspect-ratio-line', 
                    title: language === 'zh' ? '更大的空間自由度' : 'Greater Spatial Freedom',
                    desc: 'Greater Spatial Freedom'
                  },
                  { 
                    icon: 'ri-hd-line', 
                    title: language === 'zh' ? '更高端的影像技術' : 'Advanced Imaging Technology',
                    desc: 'Advanced Imaging Technology'
                  },
                  { 
                    icon: 'ri-speed-up-line', 
                    title: language === 'zh' ? '更高效的製作流程' : 'More Efficient Production',
                    desc: 'More Efficient Production'
                  },
                  { 
                    icon: 'ri-global-line', 
                    title: language === 'zh' ? '更具國際視野的創作平台' : 'International Creative Platform',
                    desc: 'International Creative Platform'
                  }
                ].map((commitment, index) => (
                  <div key={index} className={`p-4 md:p-6 transition-all duration-300 hover:scale-105 hover:rotate-1 border ${
                    isDarkMode 
                      ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                      : 'bg-white border-gray-200 shadow-sm'
                  }`}>
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 md:mb-4">
                      <i className={`${commitment.icon} text-black text-base md:text-xl`}></i>
                    </div>
                    <h4 className={`text-sm md:text-base font-bold mb-2 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{commitment.title}</h4>
                    {language === 'zh' && (
                      <p className={`text-xs md:text-sm ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-500'
                      }`}>{commitment.desc}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Brand Slogan */}
              <div className={`p-6 md:p-8 border ${
                isDarkMode 
                  ? 'bg-black/40 backdrop-blur-sm border-white/10' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FFFF00]/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-movie-2-line text-black text-base md:text-xl"></i>
                  </div>
                  <p className={`text-xl md:text-2xl lg:text-3xl font-black ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {language === 'zh' ? (
                      <>
                        BumbleBee101&nbsp;
                        <span className="bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent">
                          為影像創作而生，
                        </span>
                        為新一代製作而存在。
                      </>
                    ) : (
                      <>
                        BumbleBee101&nbsp;
                        <span className="bg-gradient-to-r from-[#FFFF00] to-[#E6E600] bg-clip-text text-transparent">
                          is built for visual creation
                        </span>
                        &nbsp;— and for the next generation of production.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 md:py-16 ${
        isDarkMode 
          ? 'bg-gradient-to-b from-gray-900/80 to-black backdrop-blur-sm text-white' 
          : 'bg-gradient-to-b from-gray-800 to-gray-900 text-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 mb-8 md:mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/a6359b9bd20651dafb0211369ea1bd13/438f2387c77f939110bc8482f2d2d85c.png" 
                  alt="Studio Logo"
                  className="h-6 md:h-8 w-auto"
                />
              </div>
            </div>

            <div>
              <h4 className={`font-semibold mb-3 md:mb-4 uppercase tracking-wider text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-200'
              }`}>Newsletter</h4>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder={language === 'zh' ? "訂閱最新場地資訊" : "Subscribe for latest studio updates"}
                  className={`flex-1 bg-transparent border-b py-2 text-xs focus:outline-none transition-colors ${
                    isDarkMode
                      ? 'border-gray-600 text-white placeholder-gray-400 focus:border-[#FFFF00]'
                      : 'border-gray-500 text-white placeholder-gray-300 focus:border-[#FFFF00]'
                  }`}
                />
                <button className="ml-2 text-[#FFFF00] hover:text-white">
                  <i className="ri-arrow-right-line text-base"></i>
                </button>
              </div>
            </div>

            <div>
              <h4 className={`font-semibold mb-3 md:mb-4 uppercase tracking-wider text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-200'
              }`}>Quick Links</h4>
              <div className="space-y-1 md:space-y-2">
                <button 
                  onClick={() => scrollToSection('overview')}
                  className={`block transition-colors text-xs ${
                    isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {language === 'zh' ? '場地介紹' : 'Studio Overview'}
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className={`block transition-colors text-xs ${
                    isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {language === 'zh' ? '服務項目' : 'Services'}
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className={`block transition-colors text-xs ${
                    isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {language === 'zh' ? '聯絡我們' : 'Contact Us'}
                </button>
              </div>
            </div>

            <div>
              <h4 className={`font-semibold mb-3 md:mb-4 uppercase tracking-wider text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-200'
              }`}>Follow Us</h4>
              <div className="space-y-1 md:space-y-2">
                <a href="#" className={`block transition-colors text-xs ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                }`}>Instagram</a>
                <a href="#" className={`block transition-colors text-xs ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                }`}>Facebook</a>
                <a href="#" className={`block transition-colors text-xs ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                }`}>YouTube</a>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className={`flex justify-center items-center space-x-4 text-xs ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              <span>© 2026 BumbleBee101 Ltd. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;